/**
 * HTML Card theme contract
 *
 * Semantic CSS variables injected into sandboxed chat HTML cards.
 * Fragments must use only these tokens — never hard-code light/dark colors.
 */

export const HTML_CARD_THEME_VARS = [
  '--losi-card-bg',
  '--losi-card-surface',
  '--losi-card-surface-muted',
  '--losi-card-text',
  '--losi-card-text-secondary',
  '--losi-card-border',
  '--losi-card-accent',
  '--losi-card-on-accent',
  '--losi-card-focus',
  '--losi-card-shadow'
] as const

export type HtmlCardThemeVar = (typeof HTML_CARD_THEME_VARS)[number]

export type HtmlCardTheme = Record<HtmlCardThemeVar, string>

/** Resolve live theme tokens from the host document (falls back safely). */
export function resolveHtmlCardTheme(
  root: HTMLElement | null = typeof document !== 'undefined' ? document.documentElement : null
): HtmlCardTheme {
  const styles = root ? getComputedStyle(root) : null
  const read = (token: string, fallback: string) => {
    const raw = styles?.getPropertyValue(token)?.trim()
    return raw || fallback
  }

  // Map shadcn HSL tokens into absolute colors usable inside the iframe.
  const hsl = (token: string, fallback: string) => {
    const value = read(token, '')
    if (!value) return fallback
    return `hsl(${value})`
  }

  return {
    '--losi-card-bg': hsl('--background', '#f4f4f5'),
    '--losi-card-surface': hsl('--card', '#ffffff'),
    '--losi-card-surface-muted': hsl('--muted', '#f4f4f5'),
    '--losi-card-text': hsl('--foreground', '#18181b'),
    '--losi-card-text-secondary': hsl('--muted-foreground', '#71717a'),
    '--losi-card-border': hsl('--border', '#e4e4e7'),
    '--losi-card-accent': hsl('--primary', '#0ea5e9'),
    '--losi-card-on-accent': hsl('--primary-foreground', '#ffffff'),
    '--losi-card-focus': hsl('--ring', '#0ea5e9'),
    '--losi-card-shadow': '0 1px 2px rgba(0,0,0,0.06)'
  }
}

export function themeToCssText(theme: HtmlCardTheme): string {
  return Object.entries(theme)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n  ')
}

/** Shared base styles every HTML card receives (native inputs follow theme). */
export function buildHtmlCardBaseStyles(theme: HtmlCardTheme): string {
  return `
:root {
  ${themeToCssText(theme)}
  color-scheme: light dark;
}
html, body {
  margin: 0;
  padding: 0;
  background: var(--losi-card-bg);
  color: var(--losi-card-text);
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  font-size: 15px;
  line-height: 1.45;
}
* { box-sizing: border-box; }
button, input, select, textarea {
  font: inherit;
  color: inherit;
}
input[type="range"],
input[type="radio"],
input[type="checkbox"],
input[type="color"],
input[type="date"],
input[type="time"] {
  accent-color: var(--losi-card-accent);
}
.losi-card-root {
  background: var(--losi-card-surface-muted);
  color: var(--losi-card-text);
  border-radius: 16px;
  padding: 14px;
}
.losi-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--losi-card-accent);
  margin: 0 0 6px;
}
.losi-muted {
  color: var(--losi-card-text-secondary);
  font-size: 13px;
}
.losi-btn {
  appearance: none;
  border: 0;
  border-radius: 12px;
  background: color-mix(in srgb, var(--losi-card-accent) 18%, transparent);
  color: var(--losi-card-text);
  font-weight: 600;
  padding: 10px 14px;
  cursor: pointer;
}
.losi-btn:focus-visible {
  outline: 2px solid var(--losi-card-focus);
  outline-offset: 2px;
}
.losi-field {
  width: 100%;
  border: 1px solid var(--losi-card-border);
  background: var(--losi-card-surface);
  border-radius: 12px;
  padding: 10px 12px;
}
.losi-field:focus {
  outline: 2px solid var(--losi-card-focus);
  outline-offset: 1px;
}
`.trim()
}
